import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Shield, RotateCw, Check, Bot, Crown } from 'lucide-react';
import { BidRecord, SeatPosition, TablePlayer } from '../../state/ArenaTypes';
import { AuctionTurnTimerBadge } from './AuctionTurnTimerBadge';
import { GameTableConfig } from '../../engine/GameTableConfig';
import { useArenaStore } from '../../store/useArenaStore';
import { arenaAudio } from '../../services/ArenaAudioService';

export interface SpadesBiddingHudProps {
  currentHighBidRecord?: BidRecord | null;
  isMyTurnToBid: boolean;
  onSubmitBid: (bid: number, direction: undefined, category: 'TRUMP') => void;
  onPass?: () => void;
  bids?: BidRecord[];
  seats?: (TablePlayer | null)[];
  dealerSeat?: SeatPosition;
  mySeat?: SeatPosition | null;
  isActionPending?: boolean;
  turnTimerSeconds?: number | null;
  turnTimerMax?: number;
  config?: GameTableConfig | null;
}

const SpadesBiddingHudComponent: React.FC<SpadesBiddingHudProps> = ({
  isMyTurnToBid,
  onSubmitBid,
  bids = [],
  seats = [],
  dealerSeat,
  mySeat,
  isActionPending = false,
  turnTimerSeconds,
  turnTimerMax = 40,
  config,
}) => {
  const storeConfig = useArenaStore((s) => s.gameConfig);
  const effectiveConfig = config ?? storeConfig;
  if (!effectiveConfig) return null;

  const bidding = effectiveConfig.bidding;
  const minBid = bidding.minBid ?? 0;
  const maxBid = bidding.maxBid ?? 13;
  const nilBonus = bidding.nilBonusPoints ?? 100;
  const nilPenalty = bidding.nilPenaltyPoints ?? 100;
  const eligibleBidLevels = Array.from({ length: Math.max(1, maxBid - minBid + 1) }, (_, i) => minBid + i);

  const trickNoun = effectiveConfig.ui?.trickNoun || 'trick';
  const trickNounPlural = effectiveConfig.ui?.trickNounPlural || 'tricks';
  const themeSymbol = effectiveConfig.ui?.themeSymbol || '♠';

  const defaultLevel = eligibleBidLevels.includes(3) ? 3 : (eligibleBidLevels[0] ?? minBid);
  const [selectedBid, setSelectedBid] = useState<number>(defaultLevel);
  const [hasSubmittedLocally, setHasSubmittedLocally] = useState(false);

  React.useEffect(() => {
    if (!isMyTurnToBid) {
      setHasSubmittedLocally(false);
    }
  }, [isMyTurnToBid]);

  React.useEffect(() => {
    if (!hasSubmittedLocally || !isMyTurnToBid) return;
    const timer = setTimeout(() => {
      setHasSubmittedLocally(false);
    }, 4500);
    return () => clearTimeout(timer);
  }, [hasSubmittedLocally, isMyTurnToBid]);

  if (!isMyTurnToBid || mySeat === null || mySeat === undefined) {
    return null;
  }

  // Find partner's seat & bid (Partner is opposite: seat + 2 % 4)
  const partnerSeat = ((mySeat + 2) % 4) as SeatPosition;
  const partnerPlayer = seats[partnerSeat];
  const partnerBidRecord = (bids || []).find((b) => b && b.seat === partnerSeat);
  const partnerBid = partnerBidRecord ? partnerBidRecord.bid : null;
  const teamTotal = partnerBid !== null ? partnerBid + selectedBid : selectedBid;

  // Fixed perspective order: You (South) -> West -> Partner (North) -> East
  const displayPlayerOrder = [
    mySeat as SeatPosition,
    ((mySeat + 1) % 4) as SeatPosition,
    ((mySeat + 2) % 4) as SeatPosition,
    ((mySeat + 3) % 4) as SeatPosition
  ];

  const handleSelectLevel = (level: number) => {
    arenaAudio.playCardSelect();
    setSelectedBid(level);
  };

  const handleConfirm = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (isActionPending || hasSubmittedLocally) return;
    setHasSubmittedLocally(true);
    arenaAudio.playBidChime();
    onSubmitBid(selectedBid, undefined, 'TRUMP');
  };

  const isSubmitting = hasSubmittedLocally || isActionPending;
  const maxTimer = turnTimerMax && turnTimerMax > 0 ? turnTimerMax : 40;
  const redThreshold = Math.max(5, Math.round(maxTimer * 0.25));
  const isCritical = turnTimerSeconds !== undefined && turnTimerSeconds !== null && turnTimerSeconds <= redThreshold;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95, y: 16, x: '-50%' }}
      animate={{
        opacity: hasSubmittedLocally ? 0.75 : 1,
        scale: hasSubmittedLocally ? 0.98 : 1,
        y: 0,
        x: '-50%'
      }}
      exit={{
        opacity: 0,
        scale: 0.95,
        y: 12,
        x: '-50%',
        transition: { duration: 0.2, ease: [0.32, 0, 0.67, 0] }
      }}
      transition={{ type: 'spring', damping: 24, stiffness: 300, mass: 0.8 }}
      className="absolute bottom-[178px] sm:bottom-[198px] md:bottom-[218px] left-1/2 z-40 w-[calc(100%-36px)] max-w-[430px] sm:max-w-[455px] pointer-events-auto select-none rounded-[28px] bg-[#0a1120] transition-[border-color,box-shadow] duration-300 transform-gpu [backface-visibility:hidden] [webkit-backface-visibility:hidden] acs-font-scale-110"
      style={{
        border: isCritical ? '1.5px solid #f43f5e' : '1.5px solid #d48b28',
        boxShadow: isCritical
          ? '0 0 10px 1px rgba(244, 63, 94, 0.5), 0 16px 45px rgba(0, 0, 0, 0.85)'
          : '0 0 10px 1px rgba(229, 142, 25, 0.35), 0 16px 45px rgba(0, 0, 0, 0.85)',
        WebkitBackfaceVisibility: 'hidden',
        willChange: 'transform, opacity',
      }}
    >
      {/* CIRCULAR TIMER BADGE SITTING ON TOP OF THE BORDER */}
      <AuctionTurnTimerBadge
        turnTimerSeconds={turnTimerSeconds}
        turnTimerMax={turnTimerMax}
        className="absolute -top-7 left-1/2 -translate-x-1/2 z-30"
      />

      {/* Clean, crisp card container */}
      <div className="w-full h-full rounded-[27px] px-2.5 sm:px-3 pt-8 sm:pt-9 pb-2 sm:pb-2.5 relative z-10 flex flex-col space-y-1.5 sm:space-y-2 bg-gradient-to-b from-[#0e1728] via-[#09101d] to-[#050912]">
        {/* Watermarks clipped inside inner background layer */}
        <div className="absolute inset-0 rounded-[27px] overflow-hidden pointer-events-none select-none">
          {/* Classic Spade Silhouette */}
          <svg
            className="absolute top-0.5 left-1 w-20 h-20 sm:w-24 sm:h-24 rotate-[24deg] text-slate-300/[0.08] select-none pointer-events-none"
            viewBox="0 0 100 100"
            fill="currentColor"
          >
            <path d="M 50 15 C 46 28 14 50 14 66 C 14 78 26 84 42 78 C 39 83 34 89 26 93 L 74 93 C 66 89 61 83 58 78 C 74 84 86 78 86 66 C 86 50 54 28 50 15 Z" />
          </svg>

          {/* Secondary Echo Spade */}
          <svg
            className="absolute top-11 left-10 sm:top-12 sm:left-12 w-9 h-9 sm:w-11 sm:h-11 rotate-[22deg] text-slate-300/[0.045] select-none pointer-events-none"
            viewBox="0 0 100 100"
            fill="currentColor"
          >
            <path d="M 50 15 C 46 28 14 50 14 66 C 14 78 26 84 42 78 C 39 83 34 89 26 93 L 74 93 C 66 89 61 83 58 78 C 74 84 86 78 86 66 C 86 50 54 28 50 15 Z" />
          </svg>

          {/* Two-Tiered Crown Silhouette */}
          <svg
            className="absolute top-0.5 right-1 w-22 h-20 sm:w-25 sm:h-22 -rotate-[26deg] select-none pointer-events-none"
            viewBox="0 0 100 100"
          >
            <defs>
              <linearGradient id="crownWatermarkGradSpadesHud" x1="20%" y1="0%" x2="80%" y2="100%">
                <stop offset="0%" stopColor="#fde68a" stopOpacity="0.14" />
                <stop offset="45%" stopColor="#f59e0b" stopOpacity="0.08" />
                <stop offset="100%" stopColor="#b45309" stopOpacity="0.02" />
              </linearGradient>
            </defs>
            <path
              fill="url(#crownWatermarkGradSpadesHud)"
              d="M 14 83 Q 50 86 86 83 L 86 91 Q 50 94 14 91 Z"
            />
            <path
              fill="url(#crownWatermarkGradSpadesHud)"
              d="M 16 76 L 84 76 L 90 36 C 92 30 82 28 78 34 C 68 44 64 48 57 46 L 57 28 L 66 18 L 50 5 L 34 18 L 43 28 L 43 46 C 36 48 32 44 22 34 C 18 28 8 30 10 36 Z"
            />
          </svg>
        </div>

        {/* SECTION 1: TITLE WITH INTEGRATED SEPARATORS */}
        <div className="flex flex-col items-center justify-center text-center relative z-10 w-full pt-1">
          <div className="flex items-center justify-center gap-3 w-full px-1">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-amber-400/40 to-amber-400/70" />
            <h2 className="text-xl sm:text-2xl font-black tracking-tight leading-tight shrink-0">
              <span className="sr-only">Spades Bidding</span>
              <span aria-hidden="true">
                <span className="text-[#f5c544]">Your</span> <span className="text-white">Bid</span>
              </span>
            </h2>
            <div className="flex-1 h-px bg-gradient-to-l from-transparent via-amber-400/40 to-amber-400/70" />
          </div>

          {/* Subtitle / Partner callout from config */}
          {partnerBid !== null ? (
            <div className="text-[11px] text-amber-300 font-semibold mt-0.5">
              {partnerPlayer?.name || 'Partner'}: <span className="font-bold text-white">{partnerBid === 0 ? 'NIL' : partnerBid}</span> • Team Target: <span className="font-bold text-emerald-400">{teamTotal} {teamTotal === 1 ? trickNoun : trickNounPlural}</span>
            </div>
          ) : (
            <div className="text-[11px] text-slate-300/80 font-semibold mt-0.5">
              Select expected {trickNounPlural} ({minBid}–{maxBid})
            </div>
          )}
        </div>

        {/* SECTION 2: 4-PLAYER ROW WITH OUTER AND INNER BORDER LINES */}
        <div className="w-full rounded-2xl bg-[#081220]/70 border border-white/15 shadow-[inset_0_1px_2px_rgba(255,255,255,0.06),0_4px_16px_rgba(0,0,0,0.3)] py-2 sm:py-2.5 px-1 sm:px-1.5 relative z-10">
          <div className="grid grid-cols-4 divide-x divide-white/10">
            {displayPlayerOrder.map((seatIdx) => {
              const p = seats?.[seatIdx];
              const isMe = seatIdx === mySeat;
              const seatBid = bids.filter(b => b.seat === seatIdx).pop();
              const isDealer = seatIdx === dealerSeat;

              return (
                <div
                  key={seatIdx}
                  className="flex flex-col items-center justify-center text-center px-1 sm:px-1.5 min-w-0"
                >
                  {/* Avatar with Badges */}
                  <div className="relative shrink-0">
                    <div
                      className={`w-10 h-10 sm:w-11 sm:h-11 rounded-full overflow-hidden flex items-center justify-center text-xs font-black bg-slate-900 shadow-md transition-all ${
                        isDealer
                          ? 'border-2 border-amber-400 shadow-[0_0_10px_rgba(245,158,11,0.35)] text-amber-300'
                          : seatBid
                          ? 'border-2 border-amber-400/80 text-amber-300'
                          : 'border-2 border-white/20 text-white'
                      }`}
                    >
                      {p?.isBot ? (
                        <Bot size={17} className={isMe || isDealer ? 'text-amber-400' : 'text-slate-300'} />
                      ) : p?.avatarUrl ? (
                        <img src={p.avatarUrl} alt={p.name} className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-white text-xs sm:text-sm font-bold">
                          {p?.name ? p.name.charAt(0).toUpperCase() : `S${seatIdx + 1}`}
                        </span>
                      )}
                    </div>

                    {/* Crown Icon Badge on 'You' (bottom-left) */}
                    {isMe && (
                      <div className="absolute -bottom-0.5 -left-0.5 w-4 h-4 rounded-full bg-amber-400 text-slate-950 flex items-center justify-center border border-slate-900 shadow-sm z-20">
                        <Crown size={9} className="stroke-[3] fill-slate-950" />
                      </div>
                    )}

                    {/* Dealer Badge 'D' (top-right of avatar) */}
                    {isDealer && (
                      <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-gradient-to-br from-amber-200 via-amber-400 to-amber-600 text-slate-950 text-[7.5px] font-black flex items-center justify-center border border-slate-950 shadow z-20">
                        D
                      </div>
                    )}
                  </div>

                  {/* Player Name */}
                  <span
                    className={`mt-1 text-[11px] sm:text-xs truncate leading-tight w-full max-w-[76px] sm:max-w-[86px] ${
                      isMe ? 'text-[#f5c544] font-bold' : isDealer ? 'text-amber-300 font-bold' : 'text-slate-200 font-semibold'
                    }`}
                    title={p?.name || (isMe ? 'You' : `Player ${seatIdx + 1}`)}
                  >
                    {isMe ? 'You' : (p?.name ? p.name.split(' ')[0] : `S${seatIdx + 1}`)}
                  </span>

                  {/* Status Chip (Current Bid) */}
                  <div
                    className={`mt-1 w-full max-w-[52px] sm:max-w-[60px] h-[22px] sm:h-[24px] rounded-md border flex items-center justify-center text-[10px] sm:text-[11px] font-bold leading-none shadow-inner transition-all ${
                      seatBid
                        ? 'bg-amber-400/20 border-amber-400/50 text-amber-300'
                        : 'bg-[#0a1322] border-white/10 text-slate-400'
                    }`}
                  >
                    {seatBid ? (
                      seatBid.bid === 0 ? 'NIL' : seatBid.bid
                    ) : (
                      '—'
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* SECTION 3: BID CONTROLS */}
        <form onSubmit={handleConfirm} className="flex flex-col space-y-1.5 sm:space-y-2 w-full relative z-10">
          {/* SEPARATOR LINE */}
          <div className="w-full h-px bg-gradient-to-r from-transparent via-amber-400/30 to-transparent my-0.5" />

          {/* NIL CALLOUT BANNER IF SELECTED 0 */}
          {selectedBid === 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="p-2 rounded-xl bg-amber-500/15 border border-amber-400/30 text-amber-200 text-xs flex items-center gap-2"
            >
              <Shield size={16} className="text-amber-400 shrink-0" />
              <span className="text-[11px] leading-snug">
                <strong className="text-amber-300">Nil Bid:</strong> Win exactly 0 {trickNounPlural} for <strong className="text-emerald-400">+{nilBonus} pts</strong>. Failure costs <strong className="text-rose-400">-{nilPenalty} pts</strong>.
              </span>
            </motion.div>
          )}

          {/* DYNAMIC BID LEVEL GRID (Segmented Control matching Auction HUD) */}
          <div
            className={`p-1 rounded-xl bg-[#091220]/80 border border-white/15 shadow-inner grid gap-1 relative ${
              eligibleBidLevels.length <= 7
                ? 'grid-cols-7'
                : eligibleBidLevels.length <= 10
                ? 'grid-cols-5 sm:grid-cols-10'
                : 'grid-cols-7'
            }`}
          >
            {eligibleBidLevels.map((num) => {
              const active = selectedBid === num;
              const isNil = num === 0;
              return (
                <button
                  key={`spades_bid_${num}`}
                  type="button"
                  disabled={isSubmitting}
                  onClick={() => handleSelectLevel(num)}
                  className={`relative h-9 sm:h-9.5 rounded-lg text-center transition-all flex items-center justify-center select-none font-sans ${
                    active
                      ? 'text-slate-950 font-black'
                      : 'text-white/80 hover:text-white hover:bg-white/5 font-black cursor-pointer'
                  }`}
                >
                  {active && (
                    <motion.div
                      layoutId="activeSpadesBidLevel"
                      className="absolute inset-0 bg-gradient-to-b from-[#ffd152] via-[#ffb923] to-[#f59e0b] rounded-lg shadow-[0_0_14px_rgba(245,158,11,0.45)] border border-amber-200/60"
                      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                    />
                  )}
                  <span className="relative z-10 text-xs sm:text-sm font-black leading-none">
                    {isNil ? 'NIL' : num}
                  </span>
                </button>
              );
            })}
          </div>

          {/* ACTION BUTTON (Vibrant Gold Confirm Button) */}
          <motion.button
            type="submit"
            whileHover={!isSubmitting ? { scale: 1.015 } : undefined}
            whileTap={!isSubmitting ? { scale: 0.97 } : undefined}
            disabled={isSubmitting}
            className="w-full h-10.5 sm:h-11 px-3 rounded-xl font-black text-xs sm:text-sm uppercase tracking-wider flex items-center justify-center gap-1.5 transition-all border bg-gradient-to-r from-[#ffd152] via-[#ffb923] to-[#f59e0b] hover:from-[#ffe072] hover:to-[#ffb923] text-slate-950 border-amber-200/70 shadow-[0_4px_16px_rgba(245,158,11,0.45)] cursor-pointer"
          >
            {isSubmitting ? (
              <>
                <RotateCw size={15} className="animate-spin" />
                <span>Submitting Bid...</span>
              </>
            ) : (
              <>
                <Check size={16} className="stroke-[3] shrink-0" />
                <span className="whitespace-nowrap truncate">
                  {selectedBid === 0
                    ? `Confirm Bid: NIL (0 ${trickNounPlural})`
                    : `Confirm Bid: ${selectedBid} ${selectedBid === 1 ? trickNoun : trickNounPlural}`}
                </span>
              </>
            )}
          </motion.button>

          {/* DECORATIVE FOOTER DIVIDER: ─── ♠ ─── */}
          <div className="flex items-center justify-center gap-3 w-full pt-1 pb-0.5 select-none relative z-10">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-amber-400/30 to-amber-400/50" />
            <span className="text-amber-400 text-base sm:text-lg select-none leading-none drop-shadow-[0_0_8px_rgba(245,158,11,0.5)]">
              {themeSymbol}
            </span>
            <div className="flex-1 h-px bg-gradient-to-l from-transparent via-amber-400/30 to-amber-400/50" />
          </div>
        </form>
      </div>
    </motion.div>
  );
};

export const SpadesBiddingHud = React.memo(SpadesBiddingHudComponent);
